import cachedGetJSON from './cachedFetch';
import { toCsv, mergeResponses } from './util';

/**
 * Craft and make API calls
 * @module util/apiCall
 */

/**
 * encode parameters into a query string
 * @private
 * @function encodeParams
 * @param {Object} params     the parameters to encode into a query string
 * @returns {String}          a query string with the parameters URI encoded
 * @memberof util/apiCall
 */
export function encodeParams(params = {}) {
	return Object.keys(params).map((key, index) => {
		const delimeter = index === 0 ? '?' : '&';
		const param = params[key];

		return `${delimeter}${encodeURIComponent(key)}=${encodeURIComponent(param)}`;
	}).join('');
}

/**
 * Wrapper around cachedGetJSON() that fetches cloudquote URLs.
 * @function apiCall
 * @param {String} api       the endpoint to make a request to
 * @param {Object} params    parameters for the query string of the request
 * @returns {Promise}        a Promise which resolves when the request completes, rejects if response status !== 200
 * @memberof util/apiCall
 */
export function apiCall(api, params) {
	if ('symbol' in params && !params.symbol) {
		return Promise.reject(Error('Symbol is required'));
	}
	params.symbol && (params.symbol = params.symbol.toUpperCase());
	return cachedGetJSON(getApiUrl(api, params));
}

/**
 * Encode an endpoint and parameters into a CloudQuote API URL
 * @function getApiUrl
 * @param {String} api       the endpoint to make a request to
 * @param {Object} params    parameters for the query string of the request
 * @returns {String}         a URL to the CloudQuote API
 * @memberof util/apiCall
 */
export function getApiUrl(api, params) {
	let query = encodeParams(params);
	return `//api.cloudquote.net${api}${query}`;
}

// Used only for determining whether the api url exceeds the max allowable length
export const PROTOCOL = 'https:';
// https://support.microsoft.com/en-us/help/208427/maximum-url-length-is-2-083-characters-in-internet-explorer
export const MAX_URL_LEN = 2048;

/**
 * @private
 * Makes one or multiple api calls for a provided set of securities based on the max URL length
 * @function makeSplitApiCalls
 * @param {String} api             the endpoint to make a request to
 * @param {String[]} symbols       array of symbols
 * @param {Object} params          parameters for the query string of the request
 * @param {Number} [maxUrlLength=2048]    a field or array of fields
 * @returns {Promise}              a Promise resolving with JSON or rejecting with error
 */
export function makeSplitApiCalls(api, symbols, params, maxUrlLength=MAX_URL_LEN) {
	let numParts = 1;
	// each array in symbolParts is an array of symobls that will be included in a GET request
	let symbolParts = [symbols];
	// split the symbols into multiple GET requests until each request meets the max URL length requirement
	while (symbolParts[0].length > 1 && symbolParts.some(s => (PROTOCOL + getApiUrl(api, { symbols: toCsv(s), ...params })).length > maxUrlLength)) {
		numParts++;
		symbolParts = [];
		let splitLength = Math.ceil(symbols.length / numParts);
		let symbolsClone = symbols.slice();
		while (symbolsClone.length) {
			symbolParts.push(symbolsClone.splice(0, Math.min(splitLength, symbolsClone.length)));
		}
	}
	return Promise.all(symbolParts.map(s => apiCall(api, { symbols: toCsv(s), ...params })));
}

/**
 * Makes one or multiple api calls and aggregates into a single response
 * @function apiCallWithManySymbols
 * @param {String} api             the endpoint to make a request to
 * @param {String[]} symbols       array of symbols
 * @param {Object} params          parameters for the query string of the request
 * @returns {Promise}              a Promise resolving with JSON or rejecting with error
 * @memberof util/apiCall
 */
export function apiCallWithManySymbols(api, symbols, params) {
	return makeSplitApiCalls(api, symbols, params)
		.then(mergeResponses);
}


// WEBPACK FOOTER //
// ../node_modules/@os/cloudquote/src/util/apiCall.js